
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import { useState } from "react";
import {
  Note,
  ComponentDocs,
  Preview,
  DocsTable,
  Code,
  Link,
} from "@lighting-beetle/lighter-styleguide";
import { Button, ButtonSecondary } from "..";
import Modal from ".";
export const modalPlaceholderText = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum hendrerit auctor egestas. Mauris scelerisque velit eu ex consectetur, id lacinia est eleifend. Nunc volutpat leo nulla, nec tempor justo vulputate in. Pellentesque laoreet nunc velit, et pretium mi posuere nec. Curabitur porta justo et diam elementum ornare. Nulla accumsan nibh non facilisis ornare. Aliquam dictum a tellus et rutrum. Mauris dolor lectus, gravida non ex at, elementum feugiat ligula. Maecenas ultricies, nisl sit amet accumsan cursus, ante est semper leo, in interdum metus tellus in mauris. Quisque luctus nisl nec laoreet laoreet.";
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};

const layoutProps = {
  modalPlaceholderText
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">





    <h1>{`Modal`}</h1>
    <h2>{`Variants`}</h2>
    <h3>{`Default`}</h3>
    <Preview mdxType="Preview">
  {() => {
        const [showModal, setShowModal] = useState(false);
        return <React.Fragment>
        <Button onClick={() => setShowModal(true)} mdxType="Button">Open Modal</Button>
        {showModal && <Modal id="default-modal" onHide={() => setShowModal(false)} mdxType="Modal">
            <p className="no-mrg-bottom">{modalPlaceholderText}</p>
          </Modal>}
      </React.Fragment>;
      }}
    </Preview>
    <Note title="Non-react environments behavior" mdxType="Note">
  In non-react environments, Modal can be opened with attribute{" "}
  <Code language="html" mdxType="Code">data-a11y-dialog-show="[modal-id]"</Code> on button
  element.{" "}
  <Link href="https://github.com/edenspiekermann/a11y-dialog" mdxType="Link">
    See a11y-dialog docs
  </Link>{" "}
  for more information.
    </Note>
    <h2>{`JS module reference`}</h2>
    <p>{`Modal is backed with `}<a parentName="p" {...{
        "href": "https://github.com/edenspiekermann/a11y-dialog"
      }}>{`a11y-dialog | see docs`}</a></p>
    <p>{`If you omit `}<inlineCode parentName="p">{`data-modal`}</inlineCode>{` attribute on `}<inlineCode parentName="p">{`<div class="modal" />`}</inlineCode>{` element, select won't be initialized and you need to initialize it manually. Then you will have access to instance and you can handle modals programatically.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const instance = new window.myApp.Modal(
  document.getElementById("my-modal"),
  config
);
`}</code></pre>
    <h3>{`Config`}</h3>
    <DocsTable data={[{
      prop: "classModalIsOpen",
      type: "string",
      default: "is-active",
      description: "Class which indicates that modal is open."
    }, {
      prop: "classModalIsOpenBody",
      type: "string",
      default: "is-active",
      description: "Class on body which indicates that modal is open."
    }, {
      prop: "root",
      type: "HTMLElement",
      default: `document.getElementById('root')`,
      description: "Root of page content which should be hidden when modal is open."
    }, {
      prop: "modalsRoot",
      type: "HTMLElement",
      default: `document.getElementById('root-modals')`,
      description: "Element on page where modal is be placed. This elements should placed outside main content (`'root'` option) and usually at the and of <body /> tag."
    }, {
      prop: "onShow",
      type: "function",
      default: "() => {}",
      description: "On show callback function."
    }, {
      prop: "onHide",
      type: "function",
      default: "() => {}",
      description: "On hide callback function."
    }]} mdxType="DocsTable" />
    <h3>{`Methods`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const instance = window.myApp.Modal.getInstance(el);
/*
  instance.method();
*/
`}</code></pre>
    <DocsTable data={[{
      prop: "init",
      type: "func",
      default: "() => {}",
      description: "Initialize instance. This is called automatically on `new Plugin(el, config)`"
    }, {
      prop: "destroy",
      type: "func",
      default: "() => {}",
      description: "Destroy the instance - removes all listeners"
    }, {
      prop: "update",
      type: "func",
      default: "() => {}",
      description: "Updates an instance."
    }, {
      prop: "show",
      type: "func",
      default: "() => {}",
      description: "Shows modal programmatically."
    }, {
      prop: "hide",
      type: "func",
      default: "() => {}",
      description: "Hides modal programmatically."
    }, {
      prop: "static getInstance",
      type: "func",
      default: "element => {}",
      description: "Get instance of `Modal` attached to specific DOM element (usually `[data-modal]`)."
    }]} mdxType="DocsTable" />
    <Note title="Modal triggers" mdxType="Note">
  <strong>a11y-dialog</strong> collects all modal openers/triggers with
  querySelector and add them event listeners after page is loaded. Because of
  that, is important to keep in mind, that every new opener/trigger added to DOM
  programatically after page load <strong>will not work</strong>. Workaround is
  to use instance of Modal component and call <strong>show()</strong> method to
  open Modal window.
    </Note>
    <h2>{`Props`}</h2>
    <ComponentDocs title="<Modal />" component={Modal} mdxType="ComponentDocs" />
    <h2>{`Accessibility`}</h2>
    <p>{`Modal are built according to `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#dialog_modal"
      }}>{`WAI-ARIA Authoring Practices 1.1`}</a>{`.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;